import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './App.css';
import { useNavigate } from 'react-router-dom';
import { awardXp } from './utils/xp';

interface Word {
    id: string;
    japanese: string;
    danish: string;
}


interface WrittenQuizProps {
    onNextMode?: () => void;
    onScore?: (score: number) => void;
}

function WrittenQuiz({ onNextMode, onScore }: WrittenQuizProps) {
        console.log('Quiz component rendered', onNextMode, onScore);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const [items, setItems] = useState<Word[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [userInput, setUserInput] = useState('');
    const [score, setScore] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
    const [userAnswers, setUserAnswers] = useState<{question: string, user: string, correct: string, isCorrect: boolean}[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const username = process.env.REACT_APP_API_USERNAME;
                const password = process.env.REACT_APP_API_PASSWORD;
                const token = btoa(`${username}:${password}`);
                const response = await axios.get<Word[]>(`${process.env.REACT_APP_API_URL}/Word/AllWords`, {
                    headers: { Authorization: `Basic ${token}` },
                });
                // Shuffle and take only 25 words
                const shuffled = response.data.sort(() => 0.5 - Math.random()).slice(0, 25);
                setItems(shuffled);
            } catch (err) {
                // handle error
            }
        };
        fetchData();
    }, []);


    // Helper: Levenshtein distance
    function levenshtein(a: string, b: string): number {
        const m = a.length, n = b.length;
        const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
        for (let i = 0; i <= m; i++) dp[i][0] = i;
        for (let j = 0; j <= n; j++) dp[0][j] = j;
        for (let i = 1; i <= m; i++) {
            for (let j = 1; j <= n; j++) {
                if (a[i - 1] === b[j - 1]) {
                    dp[i][j] = dp[i - 1][j - 1];
                } else {
                    dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
                }
            }
        }
        return dp[m][n];
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!items[currentQuestion]) return;
        const correctAnswerRaw = items[currentQuestion].japanese.trim().toLowerCase();
        const input = userInput.trim().toLowerCase();

        // Accept any word inside or outside parentheses as alternative answers
        let alternatives = [correctAnswerRaw];
        const parenMatch = correctAnswerRaw.match(/(.*?)\((.*?)\)/);
        if (parenMatch) {
            // Add before, inside, and after parenthesis as alternatives
            const before = parenMatch[1].trim();
            const inside = parenMatch[2].trim();
            if (before) alternatives.push(before);
            if (inside) alternatives.push(inside);
        }
        // Also split on spaces to allow for single-word answers
        const words = correctAnswerRaw.replace(/[()]/g, ' ').split(/\s+/).filter(Boolean);
        for (const w of words) {
            if (!alternatives.includes(w)) alternatives.push(w);
        }

        let isAnswerCorrect = false;
        for (const alt of alternatives) {
            if (input === alt) {
                isAnswerCorrect = true;
                break;
            }
        }
        if (!isAnswerCorrect && input.length > 0) {
            for (const alt of alternatives) {
                const dist = levenshtein(input, alt);
                const threshold = Math.ceil(alt.length * 0.15); // 67% of the length of the correct answer
                // If the distance is less than or equal to the threshold, consider it correct
                if (dist <= threshold) {
                    isAnswerCorrect = true;
                    break;
                }
            }
        }
        setIsCorrect(isAnswerCorrect);
        setShowFeedback(true);
        setUserAnswers(prev => [...prev, {
            question: items[currentQuestion].danish,
            user: userInput,
            correct: correctAnswerRaw,
            isCorrect: isAnswerCorrect
        }]);
        if (isAnswerCorrect) {
            setScore(score + 1);
            if (onScore) onScore(1);
            awardXp();
        }
    };

    const handleNext = () => {
        setUserInput('');
        setShowFeedback(false);
        setIsCorrect(null);
        if (onNextMode) onNextMode();
        if (currentQuestion + 1 < items.length) {
            setCurrentQuestion(currentQuestion + 1);
        } else {
            if (!onNextMode) awardXp(30);
            setCompleted(true);
        }
    };

    // Focus input on new question or after feedback
    React.useEffect(() => {
        if (!showFeedback && inputRef.current) {
            inputRef.current.focus();
        }
    }, [currentQuestion, showFeedback]);

    if (items.length === 0) {
        return <p>Loading...</p>;
    }

    const incorrectCount = userAnswers.filter(ans => !ans.isCorrect).length;
    if (completed) {
        return (
            <div className="quiz-result">
                {!onNextMode && <button className="close-button" onClick={() => navigate('/')}>&times;</button>}
                <h2>Quiz færdig</h2>
                <p>Your score: {score}/{items.length}</p>
                <p style={{ color: 'red' }}>Forkerte svar: {incorrectCount}</p>
                <div>
                    {userAnswers.map((ans, idx) => (
                        <div key={idx} style={{color: ans.isCorrect ? 'green' : 'red'}}>
                            <strong>{ans.question}</strong>: Dit svar: {ans.user} | Korrekt svar: {ans.correct}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const currentItem = items[currentQuestion];

    return (
        <div className="quiz-container">
            {!onNextMode && <button className="close-button" onClick={() => navigate('/')}>&times;</button>}
            <div className="quiz-progress-bar">
              <div className="quiz-progress-fill" style={{ width: `${(currentQuestion / items.length) * 100}%` }} />
            </div>
            <h2 className="quiz-title">Oversæt til Japansk</h2>
            <p className="quiz-description">{currentItem.danish}</p>
            <form
                onSubmit={showFeedback ? (e) => { e.preventDefault(); handleNext(); } : handleSubmit}
            >
                <input
                    type="text"
                    className="word-search-input"
                    placeholder="Skriv det japanske ord..."
                    value={userInput}
                    onChange={e => setUserInput(e.target.value)}
                    disabled={showFeedback}
                    ref={inputRef}
                />
                {!showFeedback && <button type="submit" className="quiz-option">Svar</button>}
            </form>
            {showFeedback && (
                <div className={isCorrect ? 'correct' : 'incorrect'}>
                    {isCorrect ? 'Korrekt! ' : 'Forkert. '}
                    {`Svaret er: ${currentItem.japanese}`}
                    <br /><br />
                    <button onClick={handleNext} className="quiz-option">Næste</button>
                </div>
            )}
        </div>
    );
}

export default WrittenQuiz;
